// Sprint notification-center (spec D5) — bell feed state.
//
// Hydrates from GET /api/notifications on demand (bell mount) and stays
// live via the 'notification:new' socket event (wired in useSocket).
// Mark-read is optimistic: the badge clears instantly, the POST
// reconciles; on failure the next hydrate corrects.
//
// New (verified no equivalent exists — the old bell derived its dot
// from per-agent unread flags in app.store; that signal stays, this
// store adds the persistent feed).

import { create } from 'zustand';
import { api } from '../lib/api';

export type NotificationTier = 'A' | 'B' | 'C';

export interface FeedNotification {
  id: string;
  tier: NotificationTier;
  agentId: string | null;
  title: string;
  body: string;
  read: boolean;
  createdAt: string;
}

interface NotificationsState {
  notifications: FeedNotification[];
  unreadCount: number;
  hydrated: boolean;

  hydrate: () => Promise<void>;
  receive: (n: FeedNotification) => void;
  markRead: (id: string) => void;
  markAllRead: () => void;
  resetForTests: () => void;
}

const MAX_CLIENT_ROWS = 100;

export const useNotificationsStore = create<NotificationsState>((set, get) => ({
  notifications: [],
  unreadCount: 0,
  hydrated: false,

  hydrate: async () => {
    try {
      const r = await api<{ notifications?: FeedNotification[]; unreadCount?: number }>(
        '/api/notifications',
      );
      // Never trust the response shape (settings.store precedent): a
      // proxy/error page/stubbed fetch can 200 with `{}`, and writing
      // `undefined` into state would crash every consumer of
      // `notifications.length` on the next render.
      set({
        notifications: Array.isArray(r?.notifications) ? r.notifications : [],
        unreadCount: typeof r?.unreadCount === 'number' ? r.unreadCount : 0,
        hydrated: true,
      });
    } catch {
      // Endpoint unreachable — keep whatever we have; the socket path
      // still appends live rows.
      set({ hydrated: true });
    }
  },

  receive: (n) => {
    const { notifications } = get();
    if (notifications.some((x) => x.id === n.id)) return; // socket + hydrate race
    set({
      notifications: [n, ...notifications].slice(0, MAX_CLIENT_ROWS),
      unreadCount: get().unreadCount + (n.read ? 0 : 1),
    });
  },

  markRead: (id) => {
    const { notifications, unreadCount } = get();
    const target = notifications.find((n) => n.id === id);
    if (!target || target.read) return;
    set({
      notifications: notifications.map((n) => (n.id === id ? { ...n, read: true } : n)),
      unreadCount: Math.max(0, unreadCount - 1),
    });
    void api(`/api/notifications/${encodeURIComponent(id)}/read`, { method: 'POST' }).catch(() => {
      // Optimistic; next hydrate reconciles.
    });
  },

  markAllRead: () => {
    set({
      notifications: get().notifications.map((n) => ({ ...n, read: true })),
      unreadCount: 0,
    });
    void api('/api/notifications/read-all', { method: 'POST' }).catch(() => {});
  },

  resetForTests: () => set({ notifications: [], unreadCount: 0, hydrated: false }),
}));
